import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import "./SplashCover.css";

// Reveal sequence on enter:
//   1. a line grows outward from the center (up + down) — SEAM_GROW
//   2. the two "doors" then slide apart; each door keeps that line as its
//      glowing inner edge, so the seam splits into two borders that ride
//      off-screen with the halves.
const SEAM_GROW = 0.4; // seconds the line takes to draw before doors part
const doorTransition = { duration: 0.9, ease: [0.7, 0, 0.3, 1], delay: SEAM_GROW };
const leftDoorV = { closed: { x: 0 }, open: { x: "-101%", transition: doorTransition } };
const rightDoorV = { closed: { x: 0 }, open: { x: "101%", transition: doorTransition } };
// Each door's glowing inner edge. Invisible at rest; draws from the center
// (scaleY) during SEAM_GROW, then rides along with its door as it slides out.
const edgeV = {
  closed: { scaleY: 0 },
  open: { scaleY: 1, transition: { duration: SEAM_GROW, ease: "easeOut" } },
};

/**
 * Full-screen "cover" gate. Reveals the site when the visitor clicks the
 * ENTER control, clicks anywhere, scrolls down, or presses Enter/Space/↓.
 * onEnter is guaranteed to fire at most once.
 */
export default function SplashCover({ profile, onEnter }) {
  const firedRef = useRef(false);
  // The ENTER button is drawn once per door and clipped at the seam, so a bare
  // CSS :hover would only light the half the cursor is over. Hoisting the state
  // up here lets both halves highlight as one button.
  const [enterLit, setEnterLit] = useState(false);

  const handleEnter = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    onEnter();
  }, [onEnter]);

  useEffect(() => {
    const onWheel = (e) => {
      if (e.deltaY > 0) handleEnter();
    };
    const onKey = (e) => {
      if (["Enter", " ", "Spacebar", "ArrowDown"].includes(e.key)) handleEnter();
    };
    const onTouch = () => handleEnter();

    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("keydown", onKey);
    window.addEventListener("touchmove", onTouch, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("touchmove", onTouch);
    };
  }, [handleEnter]);

  // The splash content. A copy lives inside each door; because each door is
  // full-viewport-wide but clipped to its half, the two copies line up as one
  // centered layout — then split along the seam and ride out with the doors.
  // `mirror` marks the right-hand copy, which is decorative: it still takes
  // pointer events (so hovering the right half of the button works) but stays
  // out of the tab order so there's only ever one real ENTER button.
  const renderContent = (mirror) => (
    <>
      <p className="splash__eyebrow">PORTFOLIO</p>
      <h1 className="splash__name gold-text">{profile.name}</h1>

      {/* zero-height anchor between name + tagline; reactor centers on it */}
      <span className="splash__reactor-anchor" aria-hidden="true">
        <span className="splash__reactor">
          <svg viewBox="0 0 140 140">
            <g className="reactor__slow">
              <circle className="reactor__ticks" cx="70" cy="70" r="66" />
            </g>
            <circle className="reactor__ring" cx="70" cy="70" r="60" />
            <g className="reactor__med">
              <circle className="reactor__seg" cx="70" cy="70" r="52" />
            </g>
            <g className="reactor__fast">
              <circle className="reactor__dash" cx="70" cy="70" r="44" />
            </g>
            <circle className="reactor__inner" cx="70" cy="70" r="30" />
            <circle className="reactor__core" cx="70" cy="70" r="3.5" />
          </svg>
        </span>
      </span>

      <p className="splash__tagline">{profile.tagline}</p>

      <button
        type="button"
        className={`splash__enter${enterLit ? " is-lit" : ""}`}
        aria-label="Enter site"
        tabIndex={mirror ? -1 : undefined}
        onMouseEnter={() => setEnterLit(true)}
        onMouseLeave={() => setEnterLit(false)}
        onFocus={() => setEnterLit(true)}
        onBlur={() => setEnterLit(false)}
        onClick={(e) => {
          e.stopPropagation();
          handleEnter();
        }}
      >
        <span className="splash__enter-bracket" aria-hidden="true" />
        <span className="splash__enter-label">▶ ENTER</span>
        <span className="splash__enter-bracket splash__enter-bracket--r" aria-hidden="true" />
      </button>
    </>
  );

  return (
    <div className="splash" onClick={handleEnter}>
      <motion.div className="splash__door splash__door--left" variants={leftDoorV}>
        <div className="splash__face" aria-hidden="true" />
        <div className="splash__panel splash__panel--left">{renderContent(false)}</div>
        <motion.div className="splash__edge splash__edge--left" variants={edgeV} aria-hidden="true" />
      </motion.div>
      <motion.div className="splash__door splash__door--right" variants={rightDoorV}>
        <div className="splash__face splash__face--right" aria-hidden="true" />
        <div className="splash__panel splash__panel--right" aria-hidden="true">{renderContent(true)}</div>
        <motion.div className="splash__edge splash__edge--right" variants={edgeV} aria-hidden="true" />
      </motion.div>
    </div>
  );
}
